import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormService } from '../service/form.service';
import { FormTemplate, FormStatus } from '../models/form.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TransPipe } from '../../../helpers/trans/trans.pipe';
import { AlertboxComponent } from '../../../helpers/alertbox/alertbox.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-form-list',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, TitleCasePipe, MatTooltipModule, TransPipe, AlertboxComponent],
  templateUrl: './form-list.component.html',
  styleUrls: ['./form-list.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class FormListComponent implements OnInit, OnChanges {
  @Input() organizationId!: string;
  @Input() refreshTrigger?: number;
  @Output() editFormRequested = new EventEmitter<string>();

  forms: FormTemplate[] = [];
  filteredForms: FormTemplate[] = [];
  searchQuery = '';
  statusFilter = '';
  loading = false;
  errorMessage = '';
  showconfirmarchive = false;
  formToArchive: FormTemplate | null = null;

  constructor(private formService: FormService) { }

  ngOnInit(): void {
    this.loadForms();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshTrigger'] && !changes['refreshTrigger'].firstChange) {
      console.log('Refresh trigger changed, reloading forms');
      this.loadForms();
    }
  }

  loadForms(): void {
    this.loading = true;
    this.errorMessage = '';
    this.formService
      .listTemplates(this.organizationId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (forms) => {
          console.log('Raw forms from API:', forms);
          // Transform DynamoDB items to FormTemplate objects
          this.forms = (forms || []).map(item => this.transformFormItem(item));
          console.log('Transformed forms:', this.forms);
          this.filterForms();
          this.loading = false;
        },
        error: (err) => {
          this.errorMessage = 'Failed to load forms. Please try again.';
          this.loading = false;
          console.error('Error loading forms:', err);
        }
      });
  }

  transformFormItem(item: any): FormTemplate {
    // Extract template ID from sk1 (format: template_<id>_v<version>)
    const sk1Parts = (item.sk1 || '').split('_');
    const templateId = sk1Parts[1] || '';

    return {
      id: templateId,
      sk1: item.sk1, // Preserve sk1 to identify specific version
      organizationId: this.organizationId,
      name: item.name || '',
      description: item.description || '',
      status: item.status || 'draft',
      version: item.version || 1,
      tabs: typeof item.tabs === 'string' ? JSON.parse(item.tabs) : (item.tabs || []),
      createdAt: item.createdAt || '',
      updatedAt: item.updatedAt || ''
    };
  }

  filterForms(): void {
    this.filteredForms = this.forms.filter(form => {
      const matchesSearch =
        form.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (form.description && form.description.toLowerCase().includes(this.searchQuery.toLowerCase()));
      const matchesStatus = !this.statusFilter || form.status === this.statusFilter;
      return matchesSearch && matchesStatus;
    });
  }

  getTotalFields(form: FormTemplate): number {
    return form.tabs.reduce((sum, tab) => sum + tab.fields.length, 0);
  }

  createNewForm(): void {
    // Signal parent to open builder for a new form (no templateId)
    this.editFormRequested.emit('');
  }

  editForm(form: FormTemplate): void {
    console.log('Edit form:', form.id, 'version:', form.version, 'sk1:', (form as any).sk1);
    // Emit the sk1 which contains version info (format: template_<id>_v<version>)
    // This ensures we load the correct version when multiple versions exist
    const sk1 = (form as any).sk1;
    if (sk1) {
      this.editFormRequested.emit(sk1);
    } else {
      // Fallback to old behavior if sk1 is not available
      this.editFormRequested.emit(form.id);
    }
  }

  toggleArchive(form: FormTemplate): void {
    if (form.status === 'archived') {
      // Restoring from archive - Note: This may require a separate backend endpoint
      // For now, show a message that restore is not supported in v1
      alert('Restoring archived forms is not supported yet. Please create a new draft from the archived template.');
      return;
    }

    // Store form and show confirmation dialog
    this.formToArchive = form;
    this.showconfirmarchive = true;
  }

  onArchiveConfirmed(): void {
    this.showconfirmarchive = false;
    if (!this.formToArchive) return;

    this.formService
      .archiveTemplate(this.organizationId, this.formToArchive.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          console.log('Form archived successfully');
          this.loadForms();
          this.formToArchive = null;
        },
        error: (err) => {
          this.errorMessage = 'Failed to archive form.';
          console.error('Error archiving form:', err);
          this.formToArchive = null;
        }
      });
  }

  onArchiveCancelled(): void {
    this.showconfirmarchive = false;
    this.formToArchive = null;
  }

  duplicateForm(form: FormTemplate): void {
    if (!confirm(`Duplicate "${form.name}"?`)) {
      return;
    }
    const duplicate = {
      name: `${form.name} (Copy)`,
      description: form.description,
      tabs: JSON.parse(JSON.stringify(form.tabs)) // Deep copy
    };
    this.formService
      .createTemplate(this.organizationId, duplicate)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.loadForms();
          alert('Form duplicated successfully');
        },
        error: (err) => {
          this.errorMessage = 'Failed to duplicate form.';
          console.error('Error duplicating form:', err);
        }
      });
  }
}
